import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { businessProfilesApi } from '../api/business-profiles';
import { useRadioStore } from '../store/radioStore';
import { RadioHeroPlayer } from '../components/radio/RadioHeroPlayer';
import { StationGrid, type StationCardData } from '../components/radio/StationGrid';
import { StickyRadioBar } from '../components/radio/StickyRadioBar';

/**
 * The radio-first homepage.
 *
 * One component, three URL shapes:
 *   /                  → first station's default mood (or last
 *                        played, restored from sessionStorage)
 *   /r/:slug           → selected station, its default mood
 *   /r/:slug/:mood     → selected station + mood
 *
 * URL changes are picked up by an effect that asks radioStore to
 * switch streams. Switching the URL on the SAME station+mood is a
 * no-op (radioStore has its own attached-stream guard).
 */
export default function RadioHomePage() {
  const params = useParams<{ slug?: string; mood?: string }>();

  // Hero section ref — StickyRadioBar uses IntersectionObserver
  // against this to reveal itself only after the hero scrolls out.
  const heroRef = useRef<HTMLElement>(null);

  const profilesQuery = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => businessProfilesApi.getAll(),
  });

  const stations: StationCardData[] = useMemo(() => {
    return (profilesQuery.data ?? []).map((p) => ({
      slug: p.slug,
      displayName: p.displayName,
      icon: p.icon,
      description: p.description,
    }));
  }, [profilesQuery.data]);

  // Resolve current station + mood from URL, falling back to the
  // first available station / first mood listed on that station.
  const activeStation = useMemo(() => {
    if (!profilesQuery.data) return null;
    if (params.slug) {
      return profilesQuery.data.find((p) => p.slug === params.slug) ?? null;
    }
    return profilesQuery.data[0] ?? null;
  }, [params.slug, profilesQuery.data]);

  const activeMood = useMemo(() => {
    if (!activeStation) return null;
    const moods = activeStation.moodCategories ?? [];
    if (params.mood) {
      const decoded = decodeURIComponent(params.mood);
      return moods.includes(decoded) ? decoded : (moods[0] ?? null);
    }
    return moods[0] ?? null;
  }, [activeStation, params.mood]);

  // URL → radioStore sync. We don't autostart playback here — that
  // needs a user gesture. But once the user has clicked Play once
  // in this session (tracked via sessionStorage), subsequent URL
  // changes auto-start the new stream.
  const startRadio = useRadioStore((s) => s.startRadio);
  const currentSlug = useRadioStore((s) => s.profileSlug);
  const currentMood = useRadioStore((s) => s.mood);

  useEffect(() => {
    if (!activeStation || !activeMood) return;
    if (activeStation.slug === currentSlug && activeMood === currentMood) {
      return; // already on this stream — no-op
    }
    const sessionUnlocked = sessionStorage.getItem('radio-gesture-unlocked');
    if (sessionUnlocked === '1') {
      void startRadio(activeStation.slug, activeMood);
    }
    // If not unlocked, the hero's Play button will start playback
    // and set the sessionStorage flag (see RadioHeroPlayer logic
    // via startRadio's effects).
  }, [activeStation, activeMood, currentSlug, currentMood, startRadio]);

  if (profilesQuery.isLoading || !activeStation) {
    return (
      <div className="min-h-screen bg-[#0a0a14] p-6 text-white">
        Loading radio…
      </div>
    );
  }

  return (
    <>
      <section
        ref={heroRef}
        className="bg-[#0a0a14] px-4 py-6 lg:px-8"
      >
        <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-[1.2fr,1fr]">
          <RadioHeroPlayer
            profileSlug={activeStation.slug}
            profileName={activeStation.displayName}
            moods={activeStation.moodCategories ?? []}
            currentMood={activeMood}
          />
          <StationGrid stations={stations} activeSlug={activeStation.slug} />
        </div>
      </section>

      <section
        id="why-fonorex"
        className="bg-[#0a0a14] px-4 py-16 text-white/80 lg:px-8"
      >
        <div className="mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-white">
            License-free music for your business
          </h2>
          <p className="mt-4 max-w-2xl">
            Restaurants, hotels, gyms, retail — pick a station that fits your
            space and play it on the speakers. Curated AI-generated music with
            no licensing fees.
          </p>
          {/* Below-the-fold landing sections (IndustryPlaylistsSection,
              PricingSection, FeaturesSection, etc.) are composed under
              this hero in a follow-up project — out of scope here, see
              the spec's "Out of scope" section. */}
        </div>
      </section>

      <StickyRadioBar heroRef={heroRef} />
    </>
  );
}
